import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useCart } from "../context/CartContext";

export const Header = () => {
  const { lang, toggle, t } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/tienda", label: t("nav_shop") },
    { to: "/nosotras", label: t("nav_about") },
    { to: "/contacto", label: t("nav_contact") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-canvas/85 border-b border-brand-secondary/30 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-heading text-2xl md:text-3xl tracking-tight text-ink">Pasionaria</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-brand-primary">Joyería artesanal</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`text-sm tracking-wide transition-colors hover:text-brand-primary ${
                location.pathname === l.to ? "text-brand-primary" : "text-ink-soft"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            data-testid="language-toggle"
            onClick={toggle}
            className="text-sm tracking-wide text-ink-soft hover:text-brand-primary transition-colors"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative text-ink hover:text-brand-primary transition-colors"
            aria-label="cart"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-brand-primary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden text-ink"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-canvas border-t border-brand-secondary/30 mt-3">
          <nav className="flex flex-col px-6 py-4">
            {links.map((l) => (
              <button
                key={l.to}
                onClick={() => navigate(l.to)}
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className="text-left py-3 text-ink-soft hover:text-brand-primary border-b border-brand-secondary/20 last:border-0"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
